import { useEffect, useState } from 'react';
import { X, Clock, GitBranch, ChevronDown, ChevronRight, Loader2, AlertCircle } from 'lucide-react';
import { workflowRunsApi } from '../api/client';
import { StatusIcon, StatusLabel, formatDuration, formatTotalBuildDuration } from './DeployStatus';

const RUN_STATUS_STYLE = {
  success: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  running: 'bg-blue-100 text-blue-700',
};

function formatWhen(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// Slide-over panel (right side) showing past "Deploy All" runs, most recent
// first. Each run expands in place to show its wave-by-wave repo results —
// the same data the main graph shows live, just for history.
export default function WorkflowHistoryPanel({ open, onClose }) {
  const [runs, setRuns] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    workflowRunsApi.list()
      .then(({ runs }) => setRuns(runs))
      .catch(e => setError(e.error || e.message || 'Failed to load history'))
      .finally(() => setLoading(false));
  }, [open]);

  return (
    <>
      <div
        className={`fixed inset-0 bg-slate-900/20 z-40 transition-opacity ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed top-0 right-0 h-full w-[420px] max-w-[90vw] bg-white shadow-2xl z-50 flex flex-col transition-transform duration-200 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between gap-2 px-5 py-4 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0">
              <Clock size={14} className="text-blue-600" />
            </div>
            <p className="text-sm font-semibold text-slate-900">Deployment History</p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center gap-2 text-xs text-slate-500 px-5 py-5">
              <Loader2 size={12} className="animate-spin" /> Loading history…
            </div>
          )}
          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 m-4">
              <AlertCircle size={12} className="text-red-500 shrink-0 mt-0.5" />
              <span className="text-xs text-red-600">{error}</span>
            </div>
          )}
          {!loading && !error && runs && runs.length === 0 && (
            <p className="text-xs text-slate-500 px-5 py-5">No deployments yet.</p>
          )}

          {!loading && runs && runs.length > 0 && (
            <div className="divide-y divide-slate-100">
              {runs.map(run => {
                const isOpen = expandedId === run.id;
                const duration = formatTotalBuildDuration(run.wavePlan);
                return (
                  <div key={run.id}>
                    <button
                      onClick={() => setExpandedId(isOpen ? null : run.id)}
                      className="w-full flex items-center gap-2.5 px-5 py-3 hover:bg-slate-50 text-left"
                    >
                      {isOpen
                        ? <ChevronDown size={14} className="text-slate-400 shrink-0" />
                        : <ChevronRight size={14} className="text-slate-400 shrink-0" />
                      }
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 min-w-0">
                          <GitBranch size={12} className="text-slate-400 shrink-0" />
                          <span className="text-xs font-semibold text-slate-800 truncate">{run.branch}</span>
                          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 ${RUN_STATUS_STYLE[run.status] || 'bg-slate-100 text-slate-600'}`}>
                            {run.status}
                          </span>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          {formatWhen(run.startedAt)}{duration && ` · ${duration}`}
                        </p>
                      </div>
                    </button>

                    {isOpen && (
                      <div className="px-5 pb-4 pt-1 bg-slate-50/60">
                        {run.error && (
                          <p className="text-[10px] text-red-500 mb-2">{run.error}</p>
                        )}
                        <div className="space-y-3">
                          {run.wavePlan.map(wave => (
                            <div key={wave.wave}>
                              <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5">
                                Wave {wave.wave + 1}
                              </p>
                              <div className="space-y-2.5">
                                {wave.repos.map(r => (
                                  <div key={r.repoId} className="flex items-center gap-2.5">
                                    <StatusIcon status={r.status} />
                                    <span className="text-[13px] text-slate-700 truncate flex-1">{r.repoName}</span>
                                    <StatusLabel status={r.status} />
                                    {formatDuration(r.startedAt, r.finishedAt) && (
                                      <span className="text-[9px] font-mono text-slate-400 shrink-0">
                                        {formatDuration(r.startedAt, r.finishedAt)}
                                      </span>
                                    )}
                                    {r.logUrl && (
                                      <a
                                        href={r.logUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[9px] font-mono text-blue-500 hover:text-blue-600 underline underline-offset-2 shrink-0"
                                      >
                                        logs
                                      </a>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
